import _ from "lodash";
import { useState, useEffect, useMemo } from "react";
import { ProgressBar } from "react-bootstrap";
import { Tooltip } from "@mui/material";
import { MethodColors } from "data/methods";
import Methods from "data/firestore/methods";
import AnalyticsService from "data/firestore/analytics";

// ============================================================================
// Category Breakdown Logic
// ============================================================================

interface MethodsService {
  getCategoryForMethod(methodName: string): string | null;
  getOfficialMethodName(methodName: string): string | null;
}

/**
 * Group methods by category and calculate totals
 */
function groupByCategory(
  methodsData: Record<string, number>,
  methodsService: MethodsService
): {
  categoryTotals: Record<string, number>;
  categoryMethods: Record<string, MethodDataItem[]>;
} {
  const categoryTotals: Record<string, number> = {};
  const categoryMethods: Record<string, MethodDataItem[]> = {};

  _.forEach(methodsData, (duration, methodName) => {
    const category = methodsService.getCategoryForMethod(methodName) || "Other";
    const officialName =
      methodsService.getOfficialMethodName(methodName) || methodName;

    if (!categoryTotals[category]) {
      categoryTotals[category] = 0;
      categoryMethods[category] = [];
    }
    categoryTotals[category] += duration;

    // Find existing method or add new one
    const existingMethod = categoryMethods[category].find(
      (m) => m.name === officialName
    );
    if (existingMethod) {
      existingMethod.duration += duration;
    } else {
      categoryMethods[category].push({
        name: officialName,
        duration: duration,
        percentage: 0,
      });
    }
  });

  return { categoryTotals, categoryMethods };
}

/**
 * Process category data with percentages and filtering
 */
function processCategories(
  categoryTotals: Record<string, number>,
  categoryMethods: Record<string, MethodDataItem[]>,
  totalDuration: number,
  minPercentage: number = 4.5
): CategoryDataItem[] {
  return _.chain(categoryTotals)
    .map((categoryDuration, categoryName) => ({
      category: categoryName,
      duration: categoryDuration,
      percentage: (categoryDuration / totalDuration) * 100,
      methods: categoryMethods[categoryName].map((method) => ({
        ...method,
        percentage: (method.duration / totalDuration) * 100,
      })),
    }))
    .filter((category) => category.percentage >= minPercentage)
    .orderBy(["duration"], ["desc"])
    .value();
}

/**
 * Calculate total duration from methods data
 */
function calculateTotalDuration(methodsData: Record<string, number>): number {
  return _.sum(Object.values(methodsData));
}

// methodsData is a Record<string, number> where key is method name and value is duration
export type MethodsData = Record<string, number>;

interface MethodDataItem {
  name: string;
  duration: number;
  percentage: number;
}

interface CategoryDataItem {
  category: string;
  duration: number;
  percentage: number;
  methods: MethodDataItem[];
}

export interface CategoryBreakdownProps {
  methodsData: MethodsData;
  uid: string;
}

// The methods catalog is one app-wide document. Load it once and share it across
// every card so the bar can render synchronously (no per-card fetch, no null->bar
// flip that would change a card's height after Virtuoso already measured it).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let methodsInstance: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let methodsPromise: Promise<any> | null = null;
export function preloadMethods() {
  if (!methodsPromise) {
    methodsPromise = Methods.getInstance()
      .then((m) => (methodsInstance = m))
      .catch((error) => {
        console.error("Failed to load methods:", error);
        methodsPromise = null; // allow a retry
        return null;
      });
  }
  return methodsPromise;
}

const CategoryBreakdown = ({ methodsData, uid }: CategoryBreakdownProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [methodsService, setMethodsService] = useState<any>(methodsInstance);
  const [open, setOpen] = useState(false);

  const showTooltips = (time = 4000) => {
    setOpen(true);
    setTimeout(() => setOpen(false), time);
  };

  useEffect(() => {
    if (methodsService) return;
    let cancelled = false;
    preloadMethods().then((m) => {
      if (!cancelled && m) setMethodsService(m);
    });
    return () => {
      cancelled = true;
    };
  }, [methodsService]);

  // Derived synchronously: when the catalog is already loaded (the common case
  // after preload) the bar is correct on the very first render.
  const categoryData = useMemo<CategoryDataItem[]>(() => {
    if (!methodsData || !methodsService) return [];
    const totalDuration = calculateTotalDuration(methodsData);
    if (totalDuration === 0) return [];
    const { categoryTotals, categoryMethods: catMethods } = groupByCategory(
      methodsData,
      methodsService
    );
    return processCategories(categoryTotals, catMethods, totalDuration, 4.5);
  }, [methodsData, methodsService]);

  const getCategoryColor = (categoryName: string): string => {
    const colorMap = (MethodColors as Record<string, Record<number, string>>)[
      categoryName
    ];
    if (colorMap && colorMap[500]) {
      return colorMap[500];
    }
    console.warn(`No color found for category: ${categoryName}`);
    // Fallback to a default color
    return "#6c757d";
  };

  if (!methodsData || categoryData.length === 0) {
    return null;
  }

  return (
    <ProgressBar
      style={{ display: "flex", height: 10, width: "100%" }}
      onClick={() => {
        showTooltips();
        AnalyticsService.event("see_category_breakdown_for_user", {
          for_user: uid,
        });
      }}
    >
      {categoryData.map((category, index) => (
        <Tooltip
          key={index}
          open={open}
          title={`${category.category}: ${category.percentage.toFixed(0)}%`}
          placement={index % 2 === 0 ? "top" : "bottom"}
          arrow
        >
          <ProgressBar
            now={100}
            isChild={true}
            style={{
              backgroundColor: getCategoryColor(category.category),
              borderRadius:
                index === 0
                  ? "4px 0 0 4px"
                  : index === categoryData.length - 1
                  ? "0 4px 4px 0"
                  : "0",
              height: "100%",
              flex: `0 0 ${category.percentage}%`,
            }}
          />
        </Tooltip>
      ))}
    </ProgressBar>
  );
};

export default CategoryBreakdown;
